<script setup lang="ts">
import { storeToRefs } from "pinia";
import {defineAsyncComponent} from "vue";

const menusStore = useMenusStore();
const settingsStore = useSettingsStore();

const SocialComponent = defineAsyncComponent(() => import('~/components/SocialComponents.vue'))

const { menu: menus } = storeToRefs(menusStore);
const { _settings:settings } = storeToRefs(settingsStore);

const codes = ["bottom_1", "bottom_2", "bottom_3"];

const list = computed(() =>
  (menus.value ?? [])
    .filter((i: any) => codes.includes(i.position))
    .sort(
      (a: any, b: any) => codes.indexOf(a.position) - codes.indexOf(b.position),
    ),
);

const year = new Date().getFullYear();
</script>

<template>
  <footer class="site-footer">
    <div class="container-xxl">
      <div class="footer-top">
        <div class="row g-4">
          <!-- Brand -->
          <div class="col-12 col-md-3">
            <div class="footer-brand">
              <NuxtLinkLocale to="/" class="brand-link" aria-label="Home">
                <picture>
                  <source media="(min-width: 700px)" :srcset="settings?.logo" />

                  <img
                    class="brand-logo"
                    :src="settings?.logo"
                    :alt="settings?.site_title || 'Logo'"
                    loading="lazy"
                    decoding="async"
                    :width="settings?.logo_width"
                    :height="settings?.logo_height"
                  />
                </picture>
              </NuxtLinkLocale>
            </div>
            <div class="follow-us">
              <ClientOnly>
                <SocialComponent :title="$t('follow_us')" />
              </ClientOnly>
            </div>
          </div>

          <!-- Menus -->
          <div v-for="(item, ik) in list" :key="ik" class="col-12 col-md-3">
            <div class="footer-widget">
              <h2 class="footer-widget-title">{{ item.name }}</h2>
              <ul class="footer-menu">
                <li v-for="(i, idx) in item.menu_items" :key="idx">
                  <NuxtLinkLocale class="footer-link" :to="i.url">
                    {{ i.name }}
                  </NuxtLinkLocale>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="footer-bottom mt-4 mt-lg-5 py-1">
      <div class="bottom-center text-center">
        <span> © {{ year }} {{ settings?.site_title }} </span> |
        <a
          class="footer-credit"
          href="https://webixty.com"
          target="_blank"
          rel="noopener noreferrer"
          v-html="settings.footer_text"
        />
      </div>
    </div>
  </footer>
</template>

<style scoped lang="scss">
.site-footer {
  --line: #e5e7eb; /* gray-200 */
  --focus: #111827; /* без "цветни" outlines */

  background: var(--footer-bg);
  color: var(--footer-color);
  border-top: 1px solid var(--line);
  font-size: var(--fs-0);
}
.footer-brand {
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.brand-link {
  display: inline-flex;
  align-items: center;
}

.brand-logo {
  width: 100%;
  max-width: 295px;
  height: auto;
  display: block;
}
.footer-widget {
  height: 100%;
}

.footer-widget-title {
  margin: 0 0 12px;
  font-size: var(--fs-1);
  font-weight: var(--fw-semibold);
  letter-spacing: 0.2px;
  text-transform: uppercase;
  border-bottom: 2px solid var(--el-color-primary);
  padding-bottom: 0.8rem;
}

.footer-menu {
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  gap: 10px;
}

.footer-link {
  display: inline-flex;
  align-items: center;
  color: var(--text);
  text-decoration: none;
  padding: 1px 8px;
  margin-left: -8px;
  border-radius: 10px;
  transition:
    background 160ms ease,
    color 160ms ease,
    transform 160ms ease;

  &:hover {
    background: transparent;
    color: var(--el-color-primary);
    transform: translateX(2px);
  }

  &:focus-visible {
    outline: 2px solid var(--focus);
    outline-offset: 2px;
  }
}

.footer-bottom {
  gap: 12px;
  flex-wrap: wrap;
  background: rgba(33, 33, 33, 0.05);
}

.bottom-center {
  display: flex;
  justify-content: center;
  gap: 16px;
}

.footer-credit {
  font-weight: var(--fw-semibold);
}

/* Responsive polish */
@media (max-width: 768px) {
  .site-footer {
    padding-top: 38px;
  }
  .brand-link {
    justify-content: center;
  }
  .brand-logo {
    max-width: 160px;
  }

  .footer-widget {
    padding: 0 15px 15px 15px;
  }
  .follow-us{
    padding: 0 15px 15px 15px;
  }
}
</style>
